import { NavBar } from '@design-system/NavBar';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  getBoostersAndBlockersImageUrl,
  getFoodImageUrl
} from '@utils/nutrition/getImageUrl';
import { getFormulaImageUrl } from '@utils/nutrition/getFormulaImageUrl';
import { ToggleGroup, ToggleGroupItem } from '@design-system/ToggleGroup';
import { useState, useEffect } from 'react';
import { Button } from '@design-system/Button';
import { Badge } from '@viome/viome-base/src/usecase/Home/Home.model';
import { Image } from '@design-system/Image';
import { StatusIndicator } from '@design-system/StatusIndicator';
import { FORMULA_PATH, SHOP_PATH } from '@/constants/paths';
import {
  useGetSubscriptionStatusQuery
} from '@viome/viome-base/src/usecase/Misc/MiscQuery';
import { EventTracker } from '@/utils';
import { KEYS, LocalStorage } from '@utils/LocalStorage';

type ItemType = 'Foods' | 'Ingredients' | '';

interface BoosterBlockerDetailPageProps {
  badgeItem: Badge;
  influencer: 'booster' | 'blocker';
}

export const BoosterBlockerDetailPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { badgeItem, influencer } = location?.state as BoosterBlockerDetailPageProps;
  const { data: subscriptionData } = useGetSubscriptionStatusQuery();

  const localStorageUser = LocalStorage.getItem(KEYS.BOOTUP);
  const { isEligibleForSupplements } = localStorageUser && JSON.parse(localStorageUser);
  const hasActiveSubscription = subscriptionData?.subscription_details?.has_active_subscription;


  const [category, setCategory] = useState<ItemType>('');
  const [filteredItems, setFilteredItems] = useState(badgeItem.items);

  useEffect(() => {
    handleCategoryChange(category);
  }, [badgeItem, category]);

  const handleCategoryChange = (value: ItemType) => {
    if (!value) {
      setFilteredItems(badgeItem.items);
      setCategory('');
      return;
    }

    const filtered = badgeItem.items.filter(item =>
      value === 'Foods' ? item.item_type === 'FOOD' : item.item_type === 'INGREDIENT'
    );

    setFilteredItems(filtered);
    setCategory(value);
  };

  const handleItemClick = (itemId: number, itemName: string, itemType: string) => {
    const redirectionRoute = itemType?.toLowerCase() === 'food' ? 'foods' : 'ingredients';
    EventTracker.trackEvent('nutrition', `${influencer}_${badgeItem.badge_name}_${itemName}`);
    navigate(`/nutrition/${redirectionRoute}/${itemId}`);
  };

  const handleButtonClick = () => {
    if (hasActiveSubscription) {
      navigate(FORMULA_PATH);
    } else {
      navigate(SHOP_PATH);
    }
  };

  return (
    <section className='w-full page-container'>
      <NavBar returnButtonTitle="Back" className="md:-ml-5 mt-8" />

      <div className="flex flex-col md:flex-row gap-6 mt-8 items-center md:items-start justify-between">
        <div className="flex flex-col gap-2 text-center md:text-left">
          <h1 className="typography-d2 text-primary">
            {badgeItem.badge_name}
          </h1>
          <p className="typography-body text-secondary max-w-[600px] mt-4">
            {badgeItem.badge_description}
          </p>
        </div>
        <div className="flex-shrink-0">
          <img
            src={getBoostersAndBlockersImageUrl(badgeItem.badge_icon)}
            alt={badgeItem.badge_name}
            className="w-44 h-44"
            loading='lazy'
          />
        </div>
      </div>

      <div className="mt-12">
        <p className="typography-h1 text-primary mb-6">
          Your {badgeItem.badge_name}
        </p>
        <p className="typography-body text-tertiary mb-6">
          {badgeItem.items.filter(item => item.item_type === 'FOOD').length} foods
          & {badgeItem.items.filter(item => item.item_type === 'INGREDIENT').length} ingredients
        </p>

        <ToggleGroup
          type="single"
          variant="outline"
          onValueChange={handleCategoryChange}
          value={category}
          className="mb-6"
        >
          <ToggleGroupItem
            className="px-4 py-2 bg-white typography-btn-medium"
            value="Foods"
          >
            Foods
          </ToggleGroupItem>
          <ToggleGroupItem
            className="px-4 py-2 bg-white typography-btn-medium"
            value="Ingredients"
          >
            Ingredients
          </ToggleGroupItem>
        </ToggleGroup>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-24">
          {filteredItems.length > 0 ? (
            filteredItems.map((item) => (
              <div
                key={item.item_id}
                className="w-full px-6 py-4 rounded-md bg-white flex justify-between items-center cursor-pointer hover:bg-gray-50"
                onClick={() => handleItemClick(item.item_id, item.item_name, item.item_type)}
              >
                <div className="flex items-center gap-4">
                  <Image
                    width={48}
                    height={48}
                    src={
                      item.item_type === 'FOOD'
                        ? getFoodImageUrl(item.item_image)
                        : getFormulaImageUrl(item.item_image)
                    }
                    alt={item.item_name}
                    className="rounded-full object-cover"
                  />
                  <div>
                    <h3 className="typography-body-emphasis text-primary">
                      {item.item_name}
                    </h3>
                    <div>
                      {item.item_type === 'FOOD' ? (
                        <div className='mt-3 flex items-center'>
                          <StatusIndicator color={item.rank_color} size="sm" />
                          <span className='typography-btn-medium text-tertiary ml-2.5'>
                            {item.item_rank}
                          </span>
                        </div>
                      ) : (
                        <>
                          {item?.category && (
                            <span className='typography-btn-medium text-tertiary mt-3'>
                              {item.category}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              No {category.toLowerCase()} found in this category
            </div>
          )}
        </div>
      </div>

      {isEligibleForSupplements && (
        <div className="fixed bottom-16 md:bottom-4 md:left-32 left-0 w-full flex justify-center">
          <Button
            className="min-w-[300px] px-24"
            onClick={handleButtonClick}
          >
            {hasActiveSubscription ? 'See My Formulas' : 'Shop My Formulas'}
          </Button>
        </div>
      )}
    </section>
  );
};